"use client";

import { c as _c } from "react/compiler-runtime";
import East from "@mui/icons-material/East";
import West from "@mui/icons-material/West";
import { Box, Paper, Button } from "@mui/material";
import { shallowEqual } from "@xstate/react";
import { useTranslations } from "next-intl";
import { Suspense, useCallback } from "react";
import { FeedbackButton } from "@/app/components/FeedbackButton/FeedbackButton";
import NeedAssistance from "@/app/components/NeedAssistance/NeedAssistance";
import { StepLoader } from "@/app/components/StepLoader/StepLoader";
import { useAppContext } from "@/app/contextProviders/app-context";
import { FALLBACK_FACILITY_PHONE_NUMBER } from "@/constants";
import { useSchedulerActor, useSchedulerSelector } from "@/lib/hooks/useSchedulerMachine";
import { useScrollToFirstError } from "@/lib/hooks/useScrollToFirstError";
import { useStepFocusManagement } from "@/lib/hooks/useStepFocusManagement";
import { getStepInfoFromState, getStepComponentKey, isPatientRefusedState, isSubmittingState, isValidatingPatientState } from "@/lib/machines/machine-state-mapping";
import { StepActionsProvider, useStepActions } from "@/lib/step-actions-context";
import { STEP_COMPONENTS } from "@/lib/step-component-registry";
import { StepPanel } from "./StepPanel";
import styles from "./Stepper.module.css";
function renderStepComponent(isSubmitting: boolean, StepComponent: React.ComponentType | undefined) {
  if (isSubmitting) {
    return <StepLoader />;
  }
  return StepComponent ? <StepComponent /> : null;
}
function StepperInner() {
  const $ = _c(39);
  const actorRef = useSchedulerActor();
  const {
    triggerContinue,
    hasContinueHandler,
    formId
  } = useStepActions();
  const {
    featureFlags
  } = useAppContext();
  const t = useTranslations("FooterButtons");
  let t0;
  if ($[0] !== featureFlags.disableGridLayout) {
    t0 = s => getStepInfoFromState(s, featureFlags.disableGridLayout);
    $[0] = featureFlags.disableGridLayout;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const stepInfo = useSchedulerSelector(t0, shallowEqual);
  const stepComponentKey = useSchedulerSelector(_temp);
  const isSubmitting = useSchedulerSelector(_temp2);
  const isValidatingPatient = useSchedulerSelector(_temp3);
  const isPatientRefused = useSchedulerSelector(_temp4);
  const facility = useSchedulerSelector(_temp5);
  const StepComponent = STEP_COMPONENTS[stepComponentKey];
  const isExternalNavPending = stepComponentKey === "confirmation" && !featureFlags.disableExternalConfirmation;
  useStepFocusManagement(stepInfo.stepIndex);
  const scrollToFirstError = useScrollToFirstError();
  let t1;
  if ($[2] !== formId || $[3] !== scrollToFirstError || $[4] !== triggerContinue) {
    t1 = () => {
      if (!formId) {
        triggerContinue();
      }
      scrollToFirstError();
    };
    $[2] = formId;
    $[3] = scrollToFirstError;
    $[4] = triggerContinue;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  const handleContinue = t1;
  const isContinueUnavailable = !hasContinueHandler && !formId;
  const t2 = stepInfo.stepIndex;
  let t3;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <StepLoader />;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const t4 = isSubmitting || isExternalNavPending;
  let t5;
  if ($[7] !== StepComponent || $[8] !== t4) {
    t5 = renderStepComponent(t4, StepComponent);
    $[7] = StepComponent;
    $[8] = t4;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== t5) {
    t6 = <Suspense fallback={t3}>{t5}</Suspense>;
    $[10] = t5;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== stepInfo.stepIndex || $[13] !== t6) {
    t7 = <StepPanel step={t2}>{t6}</StepPanel>;
    $[12] = stepInfo.stepIndex;
    $[13] = t6;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== actorRef || $[16] !== formId || $[17] !== handleContinue || $[18] !== isContinueUnavailable || $[19] !== isPatientRefused || $[20] !== isSubmitting || $[21] !== isValidatingPatient || $[22] !== stepInfo.label || $[23] !== stepInfo.showBackButton || $[24] !== stepInfo.showFooter || $[25] !== t) {
    t8 = stepInfo.showFooter ? <Paper className={styles.footer} elevation={0} data-stepper-footer={true}>{stepInfo.showBackButton ? <Button variant="secondary" onClick={() => actorRef.send({
        type: "BACK"
      })} startIcon={<West />} data-testid="stepper-back" disabled={isSubmitting}>{t("back")}</Button> : null}<Button variant="primary" type={formId ? "submit" : "button"} form={formId ?? undefined} onClick={handleContinue} aria-disabled={isContinueUnavailable} endIcon={<East />} data-testid="stepper-next" disabled={isSubmitting || isPatientRefused || isValidatingPatient} sx={{
        "&.Mui-disabled": {
          opacity: 0.5
        },
        "&[aria-disabled='true']": {
          opacity: 0.5,
          cursor: "not-allowed"
        }
      }}>{stepInfo.label === "reviewAndConfirm" ? t("confirmAppt") : t("continue")}</Button></Paper> : null;
    $[15] = actorRef;
    $[16] = formId;
    $[17] = handleContinue;
    $[18] = isContinueUnavailable;
    $[19] = isPatientRefused;
    $[20] = isSubmitting;
    $[21] = isValidatingPatient;
    $[22] = stepInfo.label;
    $[23] = stepInfo.showBackButton;
    $[24] = stepInfo.showFooter;
    $[25] = t;
    $[26] = t8;
  } else {
    t8 = $[26];
  }
  const t9 = stepInfo.showFooter ? `${styles.footerAssistance} ${styles.footerAssistanceWithFooter}` : styles.footerAssistance;
  const t10 = facility?.phoneNumber ?? FALLBACK_FACILITY_PHONE_NUMBER;
  let t11;
  if ($[27] !== t10) {
    t11 = <NeedAssistance phoneNumber={t10} />;
    $[27] = t10;
    $[28] = t11;
  } else {
    t11 = $[28];
  }
  let t12;
  if ($[29] !== featureFlags.disableFeedbackButton) {
    t12 = featureFlags.disableFeedbackButton ? null : <FeedbackButton className={styles.feedbackBtnMobile} />;
    $[29] = featureFlags.disableFeedbackButton;
    $[30] = t12;
  } else {
    t12 = $[30];
  }
  let t13;
  if ($[31] !== t11 || $[32] !== t12 || $[33] !== t9) {
    t13 = <Box className={t9} data-testid="stepper-assistance">{t11}{t12}</Box>;
    $[31] = t11;
    $[32] = t12;
    $[33] = t9;
    $[34] = t13;
  } else {
    t13 = $[34];
  }
  let t14;
  if ($[35] !== t13 || $[36] !== t7 || $[37] !== t8) {
    t14 = <Box className={styles.root} data-testid="scheduler-stepper">{t7}{t8}{t13}</Box>;
    $[35] = t13;
    $[36] = t7;
    $[37] = t8;
    $[38] = t14;
  } else {
    t14 = $[38];
  }
  return t14;
}
function _temp5(s_4) {
  return s_4.context.facility;
}
function _temp4(s_3) {
  return isPatientRefusedState(s_3);
}
function _temp3(s_2) {
  return isValidatingPatientState(s_2);
}
function _temp2(s_1) {
  return isSubmittingState(s_1);
}
function _temp(s_0) {
  return getStepComponentKey(s_0);
}
export function Stepper() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <StepActionsProvider><StepperInner /></StepActionsProvider>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}